import { MuiTelInput as PhoneField } from 'mui-tel-input';
import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CircularProgress,
  Collapse,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import EditLocationSchedule from '@/components/location/schedule/EditLocationSchedule';
import { isValidPhone } from '@/forms';
import { AddressSearch, MerchantSelect, type Place } from '@/forms/components';
import {
  useGetLocationForMarketingListingQuery,
  useGetLocationQuery,
  useUpdateLocationMutation,
} from '@/store/api/services/locations/location';
import {
  useCreateGmbGroupMutation,
  useGetGmbGroupsQuery,
  useUpdateMarketingListingMutation,
} from '@/store/api/services/marketing/marketing';

import type { LocationV2UpdateRequest, Merchant } from '@/types';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const UpdateLocationSchema = z.object({
  location_id: z.number(),
  name: z.string().min(2, 'Location Name is Required'),
  hours: z.array(z.string()),
  merchant_id: z.string().optional(),
  business_contact_name: z.string().optional(),
  business_contact_phone: isValidPhone(z.string().optional(), 'Invalid Phone Number'),
  vault_branch: z.string().optional(),
  service_branch: z.string().optional(),
  profile_completed: z.boolean().optional(),
  // Only rendered/submitted when opened with `showGmbGroup` (marketing listing view).
  gmb_group_id: z.string().optional(),
  // Address sub-fields are only editable in the marketing flow (showGmbGroup) — see
  // ADDRESS_FIELD_KEYS below. The per-operator flow keeps these read-only.
  street_address: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipcode: z.string().optional(),
  country: z.string().optional(),
});

type UpdateLocationSchemaType = z.infer<typeof UpdateLocationSchema>;

const ADDRESS_FIELD_KEYS = [
  'street_address',
  'address2',
  'city',
  'state',
  'zipcode',
  'country',
] as const;

type AddressFieldValues = Partial<Record<(typeof ADDRESS_FIELD_KEYS)[number], string>>;

const normalizeAddressField = (value?: string | null) => (value ?? '').trim().toLowerCase();

/**
 * Mirrors Atlas's `UpdateListingRequest.AddressMatches` — used to decide whether an address
 * edit (typed manually or picked via AddressSearch) actually needs to be submitted.
 */
export const hasAddressChanges = (
  next: AddressFieldValues,
  previous: AddressFieldValues,
): boolean =>
  ADDRESS_FIELD_KEYS.some(
    (key) => normalizeAddressField(next[key]) !== normalizeAddressField(previous[key]),
  );

/**
 * Builds the PATCH v2/listings/{id} payload — the only save call the marketing flow
 * (showGmbGroup) makes, since that endpoint is the one gated by Bitstop-operator access rather
 * than per-operator ownership. Unlike v2/locations it accepts per-field address updates or a
 * place_id — but address fields are only included when they actually changed, so an unrelated
 * name/GMB Group change doesn't also re-trigger Atlas's Google address validation call.
 */
export const buildUpdateListingPayload = (
  payload: UpdateLocationSchemaType,
  addressChanged: boolean,
  placeId: string | undefined,
) => ({
  name: payload.name,
  hours: payload.hours,
  gmb_group_id: payload.gmb_group_id || null,
  ...(payload.merchant_id !== undefined && { merchant_id: payload.merchant_id }),
  ...(payload.business_contact_name !== undefined && {
    business_contact_name: payload.business_contact_name,
  }),
  ...(payload.business_contact_phone !== undefined && {
    business_contact_phone: payload.business_contact_phone,
  }),
  ...(payload.profile_completed !== undefined && {
    profile_completed: payload.profile_completed,
  }),
  ...(addressChanged && {
    street_address: payload.street_address,
    address2: payload.address2,
    city: payload.city,
    state: payload.state,
    zipcode: payload.zipcode,
    country: payload.country,
    ...(placeId && { place_id: placeId }),
  }),
});

/**
 * `props` on ModalChildrenProps is untyped (`any`) — this documents the two shapes callers
 * actually pass: the per-operator flow (LocationsDotsMenu.tsx, Location.tsx) only ever sends
 * `data.location_id`, while the marketing flow (pages/marketing/Listing.tsx) additionally sets
 * `showGmbGroup: true` and passes the listing's current gmb_group_id/name.
 */
type EditLocationProps = {
  data?: {
    location_id?: number | string;
    gmb_group_id?: string | null;
    gmb_group_name?: string | null;
  };
  showGmbGroup?: boolean;
};

/**
 * Builds the PATCH v2/locations payload from the form values. Atlas's update endpoint has no
 * per-field address update — address changes go through `place_id` only (Atlas re-derives
 * street/city/state/etc. and coordinates from the selected place), and it has no gmb_group_id at
 * all (that's written separately, see the GMB Group call in onSubmit below). `placeId` is only
 * defined when the user picked a new address via AddressSearch this session; otherwise the
 * address is left untouched.
 */
export const buildUpdateLocationPayload = (
  payload: UpdateLocationSchemaType,
  placeId: string | undefined,
): LocationV2UpdateRequest => ({
  location_id: payload.location_id,
  name: payload.name,
  hours: payload.hours,
  ...(placeId && { place_id: placeId }),
  ...(payload.merchant_id !== undefined && { merchant_id: payload.merchant_id }),
  ...(payload.business_contact_name !== undefined && {
    business_contact_name: payload.business_contact_name,
  }),
  ...(payload.business_contact_phone !== undefined && {
    business_contact_phone: payload.business_contact_phone,
  }),
  ...(payload.vault_branch !== undefined && { vault_branch: payload.vault_branch }),
  ...(payload.service_branch !== undefined && { service_branch: payload.service_branch }),
  ...(payload.profile_completed !== undefined && {
    profile_completed: payload.profile_completed,
  }),
});

const EditLocation = ({ hide, props }: ModalChildrenProps) => {
  const { data: modalData, showGmbGroup: showGmbGroupProp } = (props ?? {}) as EditLocationProps;
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UpdateLocationSchemaType>({
    resolver: zodResolver(UpdateLocationSchema),
    mode: 'onSubmit',
  });

  const locationId = modalData?.location_id ? +modalData.location_id : skipToken;
  // GMB Group is a marketing-only concern: the per-operator edit flow never passes this prop,
  // so the section stays hidden and gmb_group_id is never submitted for those callers.
  const showGmbGroup = !!showGmbGroupProp;
  // getLocation is scoped to the caller's own operator on the backend and 404s for a location
  // belonging to a different operator, so the marketing flow (editing any operator's listing)
  // has to go through the view_any_operator-gated endpoint instead. Both hooks are called
  // unconditionally (rules of hooks) with the unused one skipped via skipToken.
  const { data: ownLocationData, isLoading: isLoadingOwnLocation } = useGetLocationQuery(
    showGmbGroup ? skipToken : locationId,
  );
  const { data: marketingLocationData, isLoading: isLoadingMarketingLocation } =
    useGetLocationForMarketingListingQuery(showGmbGroup ? locationId : skipToken);
  const locationData = showGmbGroup ? marketingLocationData : ownLocationData;
  const isLoading = showGmbGroup ? isLoadingMarketingLocation : isLoadingOwnLocation;
  const [updateLocation] = useUpdateLocationMutation();
  const [updateMarketingListing] = useUpdateMarketingListingMutation();
  const [createGmbGroup, { isLoading: isCreatingGroup }] = useCreateGmbGroupMutation();
  const { data: gmbGroupsData } = useGetGmbGroupsQuery(undefined, { skip: !showGmbGroup });
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Atlas's per-operator PATCH v2/locations still only accepts a `place_id` (no per-field
  // address update there), so for that flow the address fields stay read-only and
  // `addressPreview` is just a display preview. The marketing PATCH v2/listings/{id} endpoint
  // now accepts the fields themselves — manually typed or from AddressSearch — so for
  // `showGmbGroup` the fields are directly editable and their current form values are what get
  // submitted, not `addressPreview`.
  const [addressPreview, setAddressPreview] = useState<Place>();

  useEffect(() => {
    if (!locationData?.data) return;
    const data = locationData.data;
    const {
      merchant,
      location_id,
      location_name,
      business_contact_name,
      business_contact_phone,
      hours,
      vault_branch,
      service_branch,
    } = data;
    setValue('merchant_id', merchant?.id ?? '');
    setValue('location_id', location_id);
    setValue('name', location_name ?? '');
    setValue('business_contact_name', business_contact_name ?? '');
    setValue('business_contact_phone', business_contact_phone ?? '');
    // Never default to 24/7: a location with no stored hours must stay empty so
    // EditLocationSchedule can render it as unset, and so an untouched save leaves Atlas's hours
    // alone (both PATCH v2/locations and PATCH v2/listings ignore an empty `hours`).
    setValue('hours', hours ?? []);
    setValue('vault_branch', vault_branch ?? '');
    setValue('service_branch', service_branch ?? '');
    setValue('profile_completed', data.profile_completed ?? false);
    setValue('street_address', data.street_address ?? '');
    setValue('address2', data.address2 ?? '');
    setValue('city', data.city ?? '');
    setValue('state', data.state ?? '');
    setValue('zipcode', data.zipcode ?? '');
    setValue('country', data.country ?? '');
  }, [locationData, setValue]);

  useEffect(() => {
    if (!showGmbGroup) return;
    setValue('gmb_group_id', modalData?.gmb_group_id ?? '');
  }, [showGmbGroup, modalData?.gmb_group_id, setValue]);

  const handleCancelCreateGroup = () => {
    setShowCreateGroup(false);
    setNewGroupName('');
  };

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const result = await createGmbGroup({ name }).unwrap();
      setValue('gmb_group_id', result.data.id);
      handleCancelCreateGroup();
      toast.success(`Group "${result.data.name}" created and assigned.`);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      toast.error(
        status === 409 ? 'A group with that name already exists.' : 'Failed to create group.',
      );
    }
  };

  const handleAddressSearch = (place: Place) => {
    // Atlas re-derives address2 from Google's `subpremise` component, which Google rarely
    // returns — silently overwrite an existing suite/apt number with nothing, without warning.
    const previousAddress2 = locationData?.data.address2;
    if (previousAddress2 && !place.address2) {
      toast(
        `Heads up: Google didn't return a suite/apartment for this address, so "${previousAddress2}" will be cleared once you save.`,
        { icon: '⚠️', duration: 6000 },
      );
    }
    setAddressPreview(place);
    // In the marketing flow the address fields are directly editable — reflect the picked
    // place into form state so it's both visible and what actually gets submitted. This is a
    // no-op for the per-operator flow's disabled fields beyond updating their displayed value.
    setValue('street_address', place.street_address ?? '');
    setValue('address2', place.address2 ?? '');
    setValue('city', place.city ?? '');
    setValue('state', place.state ?? '');
    setValue('zipcode', place.zipcode ?? '');
    setValue('country', place.country ?? '');
  };

  const onSubmit = async (payload: UpdateLocationSchemaType) => {
    // The marketing flow saves everything through v2/listings/{id} in one request — it's the
    // only endpoint that works for a location belonging to a different operator than the caller
    // (the Bitstop marketing admin). v2/locations is scoped to the caller's own operator and
    // 404s for those, so it must not be called at all here; the per-operator flow keeps using it
    // exactly as before.
    if (showGmbGroup) {
      const addressChanged = hasAddressChanges(payload, locationData?.data ?? {});
      try {
        await updateMarketingListing({
          locationId: payload.location_id,
          ...buildUpdateListingPayload(payload, addressChanged, addressPreview?.place_id),
        }).unwrap();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to update location details');
        return;
      }
    } else {
      const updatePayload = buildUpdateLocationPayload(payload, addressPreview?.place_id);
      try {
        await updateLocation(updatePayload).unwrap();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to update location details');
        return;
      }
    }
    toast.success('Location details updated successfully');
    hide();
  };

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" py={2}>
        <CircularProgress />
      </Stack>
    );
  }
  return (
    <>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={2} data-test="edit-location-form">
          <TextField
            label="Location Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            {...register('name')}
          />

          <TextField
            label="Business Contact Name"
            size="small"
            fullWidth
            error={!!errors.business_contact_name}
            helperText={errors?.business_contact_name?.message}
            {...register('business_contact_name')}
          />

          <Controller
            name="business_contact_phone"
            control={control}
            render={({ field: { value, onChange } }) => (
              <PhoneField
                defaultCountry="US"
                label="Business Contact Phone"
                size="small"
                value={value}
                onChange={(_, info) => onChange(info.numberValue)}
                error={!!errors.business_contact_phone}
                helperText={errors?.business_contact_phone?.message}
              />
            )}
          />

          <Controller
            name="merchant_id"
            control={control}
            defaultValue={locationData?.data.merchant?.id}
            render={({ field: { onChange } }) => {
              const merchantData = locationData?.data.merchant;
              const [_value, setValue] = useState<Merchant | null>(
                merchantData
                  ? {
                      ...merchantData,
                      is_serviceable: false,
                      created_at: '',
                      classification:
                        (merchantData.classification as 'single_store' | 'corporate') ||
                        'single_store',
                    }
                  : null,
              );
              return (
                <MerchantSelect
                  label="Merchant"
                  labelProps={{ sx: { fontSize: 14 }, error: !!errors.merchant_id }}
                  buttonProps={{ size: 'medium' }}
                  textFieldProps={{
                    size: 'small',
                    error: !!errors.merchant_id,
                    helperText: errors.merchant_id?.message,
                  }}
                  value={_value}
                  onChange={(val) => {
                    setValue(val);
                    onChange(val?.id ?? '');
                  }}
                />
              );
            }}
          />

          {showGmbGroup && (
            <>
              <Controller
                name="gmb_group_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Google My Business Group</InputLabel>
                    <Select
                      data-test="gmb-group-select"
                      value={value ?? ''}
                      label="Google My Business Group"
                      onChange={(e) => onChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {(gmbGroupsData?.data ?? []).map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Collapse in={!showCreateGroup}>
                <Typography
                  component="button"
                  type="button"
                  data-test="gmb-group-new-toggle"
                  variant="caption"
                  color="primary.main"
                  onClick={() => setShowCreateGroup(true)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  + New group
                </Typography>
              </Collapse>

              <Collapse in={showCreateGroup}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Group name"
                    inputProps={{ 'data-test': 'gmb-group-name-input' }}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateGroup();
                      if (e.key === 'Escape') handleCancelCreateGroup();
                    }}
                    disabled={isCreatingGroup}
                    sx={{ flex: 1 }}
                  />
                  <LoadingButton
                    size="small"
                    variant="contained"
                    data-test="gmb-group-create-button"
                    loading={isCreatingGroup}
                    disabled={!newGroupName.trim()}
                    onClick={handleCreateGroup}
                    disableElevation
                  >
                    Create
                  </LoadingButton>
                  <Button
                    size="small"
                    data-test="gmb-group-cancel-button"
                    onClick={handleCancelCreateGroup}
                    disabled={isCreatingGroup}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Collapse>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/*
            Atlas's per-operator PATCH v2/locations only accepts a place_id (no per-field
            address update there), so search for a new address here to replace it entirely.
            The marketing PATCH v2/listings/{id} endpoint accepts per-field edits too, so for
            that flow (showGmbGroup) the fields below double as free-text inputs — searching
            here just pre-fills them; they stay editable before and after.
          */}
          <AddressSearch
            onPlaceSelect={handleAddressSearch}
            size="small"
            inputProps={{ 'data-test': 'address-search-input' }}
          />

          <TextField
            label="Full Address"
            size="small"
            fullWidth
            disabled
            name="full_address"
            value={locationData?.data.full_address ?? ''}
          />
          <TextField
            label="Address"
            size="small"
            fullWidth
            disabled={!showGmbGroup}
            {...register('street_address')}
          />
          <TextField
            label="Apartment, suite, etc."
            size="small"
            fullWidth
            disabled={!showGmbGroup}
            {...register('address2')}
          />
          <Stack gap={2} direction="row">
            <TextField
              label="City"
              size="small"
              fullWidth
              disabled={!showGmbGroup}
              {...register('city')}
            />
            <TextField
              label="Zip Code"
              size="small"
              fullWidth
              disabled={!showGmbGroup}
              {...register('zipcode')}
            />
          </Stack>
          <Stack gap={2} direction="row">
            <TextField
              label="State"
              size="small"
              fullWidth
              disabled={!showGmbGroup}
              {...register('state')}
            />
            <TextField
              label="Country"
              size="small"
              fullWidth
              disabled={!showGmbGroup}
              {...register('country')}
            />
          </Stack>

          {!showGmbGroup && (
            <>
              <Divider sx={{ mt: 2 }} />

              <TextField
                label="Vault branch"
                size="small"
                fullWidth
                placeholder="Branch responsible for vaulting"
                {...register('vault_branch')}
              />
              <TextField
                label="Service branch"
                size="small"
                fullWidth
                placeholder="Branch responsible for servicing"
                {...register('service_branch')}
              />
            </>
          )}

          <Controller
            name="profile_completed"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!value}
                    onChange={(_, checked) => onChange(checked)}
                  />
                }
                label="Profiles completed"
              />
            )}
          />

          <Divider sx={{ mt: 2 }} />

          <EditLocationSchedule
            initValue={locationData?.data.hours}
            onChange={(value) => setValue('hours', value)}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide()}>Discard changes</Button>
        <LoadingButton
          variant="contained"
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default EditLocation;
